import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@design-system';
import { AlertCircleIcon, ExternalLink, InfoIcon, RefreshCw, SearchIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { ContactSupportButton } from '@client/components/ContactSupportButton';
import { cn } from '@client/lib';

function JointPolicyComingSoon() {
  return (
    <div className="flex w-full items-center justify-center lg:h-[calc(100vh-320px)]">
      <Empty data-testid="joint-policy-coming-soon">
        <EmptyHeader className="max-w-md">
          <EmptyMedia>
            <div className="bg-primary-50 flex size-12 items-center justify-center rounded-full">
              <InfoIcon className="text-primary size-6" />
            </div>
          </EmptyMedia>
          <EmptyTitle>Joint Policies for Same-Sex Couples Coming Soon</EmptyTitle>
          <EmptyDescription>
            We're actively working on implementing joint policy options for same-sex couples. This
            feature will be available soon. In the meantime, please explore our single policy
            options.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    </div>
  );
}

interface NoQuotesAvailableProps {
  advisorId?: string;
}

function NoQuotesAvailable({ advisorId }: NoQuotesAvailableProps) {
  function handleCarrierSettings() {
    if (!advisorId) return;
    const url = `/advisors/${advisorId}/settings?tab=carrier-bga`;
    window.open(url, '_blank');
  }

  return (
    <Empty
      className="border-none py-12"
      data-testid="shop-insurance-no-quotes-message"
    >
      <EmptyHeader>
        <EmptyMedia>
          <div className="bg-primary-50 flex size-12 items-center justify-center rounded-full">
            <SearchIcon className="text-primary size-6" />
          </div>
        </EmptyMedia>
        <EmptyTitle>No matching quotes found</EmptyTitle>
        <EmptyDescription>
          We searched but couldn't find any quotes matching your current criteria.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="max-w-md">
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            variant="secondary"
            onClick={handleCarrierSettings}
            disabled={!advisorId}
          >
            <ExternalLink />
            Review Carrier Settings
          </Button>

          <ContactSupportButton
            subject="Missing quotes - Need assistance"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          />
        </div>
        <div className="rounded-lg bg-amber-50 p-4">
          <p className="text-xs text-amber-800">
            <strong>Note:</strong> Quote availability depends on client demographics, selected
            carriers, and product configurations.
          </p>
        </div>
      </EmptyContent>
    </Empty>
  );
}

interface QuoteErrorDisplayProps {
  error: QuoteError;
  onRetry?: () => void;
}

function QuoteErrorDisplay({ error, onRetry }: QuoteErrorDisplayProps) {
  switch (error.type) {
    case 'AGE_OUT_OF_RANGE':
      return <AgeOutOfRangeError error={error} />;
    case 'STATE_NOT_SUPPORTED':
      return <StateNotSupportedError error={error} />;
    default:
      return (
        <UnexpectedError
          error={error}
          onRetry={onRetry}
        />
      );
  }
}

interface ErrorStateProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  showErrorIcon?: boolean;
}

function ErrorState({ title, action, children, showErrorIcon }: ErrorStateProps) {
  return (
    <div className="my-auto flex flex-col items-center justify-center gap-4 py-12">
      <div
        className={cn(
          'mb-4 flex h-16 w-16 items-center justify-center rounded-full',
          showErrorIcon ? 'bg-amber-50' : 'bg-primary-50',
        )}
      >
        {showErrorIcon ? (
          <AlertCircleIcon className="h-8 w-8 text-amber-500" />
        ) : (
          <SearchIcon className="text-primary h-8 w-8" />
        )}
      </div>
      <div className="w-full max-w-md space-y-2 text-center">
        <h3 className="text-primary-dark mb-2 text-lg font-medium">{title}</h3>
        {children}
      </div>
      {action}
    </div>
  );
}

function AgeOutOfRangeError({ error }: { error: QuoteError }) {
  return (
    <ErrorState
      title="We can't show quotes for this age (yet)"
      action={
        <ContactSupportButton
          subject={`Age out of range (${error.clientAge}) - Need assistance`}
          variant="secondary"
        />
      }
    >
      <p className="mb-6 max-w-md text-center text-sm text-gray-700">
        Our current quote range is {error.minAge}&ndash;{error.maxAge}. If this client's DOB is
        correct, we can still help.
      </p>
    </ErrorState>
  );
}

function StateNotSupportedError({ error }: { error: QuoteError }) {
  const supportedStatesDisplay = error.supportedStates?.slice(0, 10).join(', ');
  const hasMoreStates = (error.supportedStates?.length || 0) > 10;

  return (
    <ErrorState
      title={`No carrier quotes found for ${error.clientState}`}
      action={
        <ContactSupportButton
          subject={`State not supported (${error.clientState}) - Need assistance`}
          variant="secondary"
        />
      }
    >
      <p className="mb-4 text-sm text-gray-700">
        Our carrier partners don't currently offer products in{' '}
        <span className="font-medium">{error.clientState}</span>. We can show quotes in:{' '}
        {supportedStatesDisplay}
        {hasMoreStates && ', and more'}.
      </p>

      <p className="mb-6 text-xs text-gray-600">
        If you added extra filters, try removing them and re-run quotes. If you already have a
        product quote in hand, we can still help you upload the policy and analyze it against the
        client's LTC plan.
      </p>
    </ErrorState>
  );
}

function UnexpectedError({ error, onRetry }: { error: QuoteError; onRetry?: () => void }) {
  const supportSubject = error.logCode
    ? `Quote loading error (ID: ${error.logCode})`
    : 'Quote loading error - Need assistance';

  return (
    <ErrorState
      showErrorIcon
      title="We're having trouble loading quotes"
      action={
        <div className="flex flex-col gap-3 sm:flex-row">
          {onRetry && (
            <Button
              variant="secondary"
              onClick={onRetry}
            >
              <RefreshCw />
              Try Again
            </Button>
          )}
          <ContactSupportButton
            subject={supportSubject}
            variant="outline"
          />
        </div>
      }
    >
      <p className="mb-6 max-w-md text-center text-sm text-gray-700">
        This looks like an issue on our side. Please refresh and try again.
        {error.logCode && (
          <>
            {' '}
            Error ID:{' '}
            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium">
              {error.logCode}
            </code>
          </>
        )}
      </p>
    </ErrorState>
  );
}

export { JointPolicyComingSoon, NoQuotesAvailable, QuoteErrorDisplay };
